import React from 'react';
import { FiltersParams, TagCategoryParams } from 'types/params';
import { FirstLevelItemObject } from 'components/Filters/context/FiltersContext/types';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';

interface UseSortingTagProps {
    tags: FirstLevelItemObject[];
    searchValue: string;
    tagsCategory: TagCategoryParams;
    filterType: FiltersParams;
}

export const useSortingTag = ({
    tags,
    searchValue,
    tagsCategory,
    filterType,
}: UseSortingTagProps) => {
    const { getFilterLabel } = useApiFiltersContext();

    // Filter tags based on search value for artist category
    const filteredTags = React.useMemo(() => {
        if (tagsCategory === TagCategoryParams.artist && searchValue.trim()) {
            return tags.filter((tag) => {
                if (!tag) return false;
                const label = getFilterLabel(
                    tag.code,
                    filterType,
                ).toLowerCase();
                const search = searchValue.toLowerCase();

                return label.startsWith(search);
            });
        }
        return tags;
    }, [tags, searchValue, tagsCategory, getFilterLabel, filterType]);

    // Group tags by first character (letter or number)
    const groupedTags = React.useMemo(() => {
        const groups: Record<string, FirstLevelItemObject[]> = {};

        filteredTags.forEach((tag) => {
            if (!tag) return;

            const label = getFilterLabel(tag.code, filterType);
            const firstChar = label.charAt(0).toUpperCase();

            // Check if it's a number
            const isNumber = /^\d$/.test(firstChar);
            const groupKey = isNumber ? '0-9' : firstChar;

            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }
            groups[groupKey].push(tag);
        });

        // Sort groups and tags within groups
        const sortedGroups: Record<string, FirstLevelItemObject[]> = {};

        Object.keys(groups)
            .sort((a, b) => {
                if (a === '0-9') return -1;
                if (b === '0-9') return 1;
                return a.localeCompare(b);
            })
            .forEach((key) => {
                sortedGroups[key] = groups[key].sort((a, b) => {
                    const labelA = getFilterLabel(a.code, filterType);
                    const labelB = getFilterLabel(b.code, filterType);

                    return labelA.localeCompare(labelB);
                });
            });

        return sortedGroups;
    }, [filteredTags, getFilterLabel, filterType]);

    return {
        filteredTags,
        groupedTags,
    };
};
